import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../api/client";
import type { DeploymentTarget, Project, PublishedAcfObject } from "../../types";
import type { AcfRow } from "./types";

interface AcfPageData {
  projects: Project[];
  rows: AcfRow[];
  targetsByObject: Record<string, DeploymentTarget[]>;
}

// Mirrors `use-snippets-data.ts`: one combined query rather than three separate loading states,
// since projects, their published ACF objects, and each object's deployment targets are always
// consumed together on this page.
async function fetchAcfPageData(): Promise<AcfPageData> {
  const { data: fetchedProjects } = await axiosInstance.get<Project[]>("/projects");

  const byObject = await Promise.all(
    fetchedProjects.map(async (project) => {
      const { data: objects } = await axiosInstance.get<PublishedAcfObject[]>(`/projects/${project.id}/acf`);
      return objects.map((object) => ({ ...object, projectName: project.name }));
    })
  );
  const rows = byObject.flat();

  const targetEntries = await Promise.all(
    rows.map(async (row) => {
      const { data } = await axiosInstance.get<DeploymentTarget[]>(`/projects/${row.projectId}/acf/${row.id}/deployments`);
      return [row.id, data] as const;
    })
  );

  return {
    projects: fetchedProjects,
    rows,
    targetsByObject: Object.fromEntries(targetEntries),
  };
}

export function useAcfData() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["acf-page"],
    queryFn: fetchAcfPageData,
  });

  return {
    projects: data?.projects ?? [],
    rows: data?.rows ?? [],
    targetsByObject: data?.targetsByObject ?? {},
    loading: isFetching,
    refetch,
  };
}
